/**
 * @file
 * Number formatting.
 *
 * @example
 * $notify($num.fixed(5000));
 * @example
 * $notify($num.formatter({ style: 'currency', currency: 'USD' })(5000));
 * @example
 * $notify($num.percent(1.2233));
 * @example
 * $notify($num.ticks(115.53125))
 * @example
 * $notify($num.fraction(4202, 4));
 */


const Intl = window.Intl;
const emDash = '—';
let dirty = 0;


const format = function(num) {
    return num === '' ? num : isNaN(num) ? emDash : this.format(num);
}

const formatToParts = function(num) {
    return num === '' ? [] : isNaN(num) ? [{ type: 'nan', value: emDash }] : this.formatToParts(num);
}

// Precompiled formatters
const percent = { style: 'percent' };
const formatters = {
    int:      [0, 0],
    decimal:  [0, 20],
    fixed:    [2, 2],
    fixed0:   [0, 0],
    fixed1:   [1, 1],
    fixed2:   [2, 2],
    fixed3:   [3, 3],
    fixed4:   [4, 4],
    fixed5:   [5, 5],
    fixed6:   [6, 6],
    fixed7:   [7, 7],
    fixed8:   [8, 8],
    percent:  [2, 2, percent],
    percent0: [0, 0, percent],
    percent1: [1, 1, percent],
    percent2: [2, 2, percent],
    percent3: [3, 3, percent],
    percent4: [4, 4, percent],
    min2:     [2, 20],
    max4:     [0, 4]
};


const $num = Vue.observable(/** @alias $num */ {
    /** Formatting configuration. */
    data: {
        /** Locale code used for number formatting. This could potentially differ from [`$locale.current`](#/global/locale). */
        locale: void 0,
        /** Grouping separator, for example `,` in American English. */
        group: void 0,
        /** Decimal separator, for example `.` in American English. */
        decimal: void 0,
        /** Percent sign with applicable spacing and direction code, for example `%` in American English. */
        percentSign: void 0
    },

    /** @ignore */
    formatter: void 0,
    /** @ignore */
    raw: void 0,
    /** @ignore */
    parse: void 0,
    /** @ignore */
    parsePercent: void 0,
    /** @ignore */
    parseTicks: void 0,
    ...formatters,

    /**
     * @private
     * Convert a number to a locale formatted string.
     *
     * Each of these shortcuts offers the `.flat` or `.sign` options - for example:
     *   `$num.decimal.flat`:     Omit thousands/lakh/crore grouping (5000.50005).
     *   `$num.decimal.sign`:     With sign if nonzero (+5,000.50005).
     *
     * These also offer `.compact` and `.flex` options. These options also themselves support `.flat` and
     * `.sign` modifiers.
     *   `$num.fixed.compact`:    Two decimal places if that will fit within 6 digits; no decimal places if below 1M;
     *                            otherwise, use compact notation with exactly 3 significant digits (1.23M).
     *   `$num.fixed.flex`:       Two decimal places if value will round to below 1M; otherwise zero decimal places.
     *
     * All of the above support the `.parts` modifier. The `.parts` modifier generates an array of objects, each of
     * which has `type` and `value` properties. This can be useful if you need to parse the result in a locale
     * independent way. The types are:
     *   `minusSign`:             A minus sign.
     *   `plusSign`:              A plus sign.
     *   `integer`:               A group of integer digits (before the decimal point).
     *   `fraction`:              A group of fractional digits (after the decimal point).
     *   `decimal`:               The decimal point.
     *   `group`:                 The group separator.
     *   `percent`:               A percent sign.
     *   `literal`:               A literal string, typically used for a non-breaking space.
     *   `compact`:               An abbreviation for compact formatting.
     *   `unit`:                  A unit descriptor (kB, MB, GB, TB) for units formatting.
     *   `nan`:                   Indicates NaN (not a number).
     *   `infinity`:              Indicates Infinity.
     *
     * To use one of the shortcut methods, you can simply put in your template something like:
     *   `div {{ $num.fixed.sign(value) }}`
     *
     * To define your own formatter, put it in your component's methods:
     *   `methods: { formatter: $num.format({ maximumFractionDigits: 2, useGrouping: false }) }`
     *
     * and in your template:
     *    `div(v-text="formatter(value)")`
     */

    _use(locale) {
        const nsample = new Intl.NumberFormat(locale, { numberingSystem: 'latn' }).formatToParts(10000.1);
        const psample = new Intl.NumberFormat(locale, { numberingSystem: 'latn', style: 'percent' }).formatToParts(0);
        const ngroup = nsample.find(part => part.type == 'group');
        const ndecimal = nsample.find(part => part.type == 'decimal');
        const group = ngroup && ndecimal ? ngroup.value : ',';
        const decimal = ngroup && ndecimal ? ndecimal.value : '.';
        const pindex = psample.findIndex(part => part.type == 'percentSign');
        let percentSign = psample[pindex].value;
        for (let pback = pindex - 1; psample[pback] && psample[pback].type == 'literal'; pback--) {
            percentSign = psample[pback].value + percentSign;
        }
        for (let pnext = pindex + 1; psample[pnext] && psample[pnext].type == 'literal'; pnext++) {
            percentSign += psample[pnext].value;
        }

        /** @ignore */
        $num.data.locale = locale;
        /** @ignore */
        $num.data.group = group;
        /** @ignore */
        $num.data.decimal = decimal;
        /** @ignore */
        $num.data.percentSign = percentSign;

        const formatter = options => {
            const self = new Intl.NumberFormat(locale, {
                numberingSystem: 'latn',
                ...options
            });
            const fn = format.bind(self);
            fn.parts = formatToParts.bind(self);
            return fn;
        }

        const flexFormatter = (digits, alt1, alt2) => {
            const threshold = 1e6 - .5 / 10 ** digits;
            const fn = num =>
                num < threshold && num > -threshold ? alt1(num) : alt2(num);
            return fn;
        }

        const compactFormatter = (formatter, digits, alt1, alt2) => {
            digits = Math.min(5, digits);
            const threshold1 = 10 ** (6 - digits) - .5 / 10 ** digits;
            const threshold2 = 1e6 - .5 / 10 ** digits;
            const fn = num =>
                num < threshold1 && num > -threshold1 ? alt1(num) :
                num < threshold2 && num > -threshold2 ? alt2(num) :
                formatter(num);
            fn.parts = num =>
                num < threshold1 && num > -threshold1 ? alt1.parts(num) :
                num < threshold2 && num > -threshold2 ? alt2.parts(num) :
                formatter.parts(num);
            return fn;
        }

        /**
         * @function
         * @description Generate a formatting function with the specified options. When possible, use one of the
         * pregenerated methods such as `$num.int` or `$num.fixed` for performance and compactness.
         * @param {Object} options - Options, as supported by `Intl.NumberFormat`.
         * @param {string} options.numberingSystem - Numbering system. This defaults to `latn` regardless of locale.
         * @param {string} options.currency - ISO 4217 currency code to use in currency formatting.
         * @param {string} options.currencyDisplay - How to display the currency in currency formatting.
         * @param {string} options.currencySign - Whether to wrap negative amounts in parenthesis rather than a minus sign, if appropriate for the current locale.
         * @param {string} options.notation - The formatting that should be displayed for the number.
         * @param {string} options.signDisplay - When to display the sign for the number; defaults to `auto`.
         * @param {string} options.style - The formatting style to use; default is `decimal`.
         * @param {string} options.useGrouping - Whether to use grouping separators, such as thousands separators or thousand/lakh/crore separators.
         */
        $num.formatter = formatter;

        /**
         * @function
         * @name int
         * @memberof $num
         * @description Format as integer portion only (5,001).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed
         * @memberof $num
         * @description Format as exactly 2 decimal places (5,000.50).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed1
         * @memberof $num
         * @description Format as exactly 1 decimal place (5,000.5).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed3
         * @memberof $num
         * @description Format as exactly 3 decimal places (5,000.500).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed4
         * @memberof $num
         * @description Format as exactly 4 decimal places (5,000.5001).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed5
         * @memberof $num
         * @description Format as exactly 5 decimal places (5,000.50005).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed6
         * @memberof $num
         * @description Format as exactly 6 decimal places (5,000.500050).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed7
         * @memberof $num
         * @description Format as exactly 7 decimal places (5,000.5000500).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name fixed8
         * @memberof $num
         * @description Format as exactly 8 decimal places (5,000.50005000).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name min2
         * @memberof $num
         * @description Format as at least 2 decimal places (5,000.50005).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name max4
         * @memberof $num
         * @description Format as up to 4 decimal places (5,000.5001).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name percent
         * @memberof $num
         * @description Format as percentage, with 2 decimal places (500,050.00%).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name percent0
         * @memberof $num
         * @description Format as percentage, with no decimal places (500,050%).
         * @param {number} value - Number to format.
         */
        /**
         * @function
         * @name percent1
         * @memberof $num
         * @description Format as percentage, with 1 decimal place (500,050.0%).
         * @param {number} value - Number to format.
         */
        const compact = {
            notation: 'compact',
            minimumSignificantDigits: 3,
            maximumSignificantDigits: 3
        };
        const compactFmt = formatter(compact);
        const compactFlat = formatter({ ...compact, useGrouping: false });
        const compactSign = formatter({ ...compact, signDisplay: 'exceptZero' });

        Object.keys(formatters).forEach(name => {
            const option = formatters[name];
            const options = {
                minimumFractionDigits: option[0],
                maximumFractionDigits: option[1],
                ...option[2]
            };
            $num[name] = formatter(options);
            $num[name].flat = formatter({ ...options, useGrouping: false });
            $num[name].sign = formatter({ ...options, signDisplay: 'exceptZero' });
            $num[name].flex = flexFormatter(option[1], $num[name], $num.int);
            $num[name].compact = compactFormatter(compactFmt, option[1], $num[name], $num.int);
            $num[name].compact.flat = compactFormatter(compactFlat, option[1], $num[name].flat, $num.int.flat);
            $num[name].compact.sign = compactFormatter(compactSign, option[1], $num[name].sign, $num.int.sign);
        });

        const rtl = $num.int(-1).includes('\u200e');

        /**
         * @function
         * @description Format a preformatted number; inserts direction codes in RTL languages or displays emdash if `undefined`.
         * @param {string} value - Number preformatted by back end. May include a prefix such as "C".
         */
        $num.raw = rtl
            ? num => num === void 0 ? emDash : (''+num)
                .replace(/\u200e?(['+-])/g, '\u200e$1')
                .replace('%', percentSign)
            : group == ',' && decimal == '.'
                ? num => num === void 0 ? emDash : (''+num)
                    .replace('%', percentSign)
                : num => num === void 0 ? emDash : (''+num)
                    .replace(/[.,]/g, ch => ch == '.' ? decimal : group)
                    .replace('%', percentSign);

        const formatBytes = [
            'byte',
            'kilobyte',
            'megabyte',
            'gigabyte',
            'terabyte',
            'petabyte'
        ].map(unit => new Intl.NumberFormat(locale, {
            numberingSystem: 'latn',
            style: 'unit',
            unit
        }));

        const intGroup = $num.int;
        const intFlat1 = $num.int.flat;
        const intFlat2 = formatter({
            minimumIntegerDigits: 2,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
        const tickSeparator = rtl ? "\u200e'" : "'";

        /**
         * @function
         * @description Format in kB, MB, GB, or TB (or local equivalent) as appropriate, with at least 2 significant
         * digits.
         * @param {number} value - Number of bytes.
         */
        $num.bytes = function(num) {
            let magnitude = 0;
            while (formatBytes[magnitude + 1] && num >= 999.5) {
                num /= 1024;
                magnitude++;
            }
            return formatBytes[magnitude].format(num.toFixed(num < 10));
        }

        /**
         * @function
         * @description Format as 1/n ticks (referred to internally as Garban pricing) with three places right of
         * apostrophe separator.
         * @param {number} value - Number to format.
         */
        $num.ticks = function(num) {
            if (isNaN(num)) {
                return emDash;
            }
            const fract = Math.abs(num % 1) * 256;
            return intFlat1(Math.trunc(num)) + tickSeparator + intFlat2(fract >>> 3) + intFlat1((fract & 7) + ((fract & 4) >>> 2));
        }

        /**
         * @function
         * @description Format as a fraction using the lowest possible denominator.
         * @param {number} num - The numerator. Must be an integer.
         * @param {number} den - The denominator. Must be an integer.
         */
        $num.fraction = function(num, den) {
            if (num !== (num | 0) || den !== (den | 0) || !den) {
                return emDash;
            }
            const value = num / den;
            const whole = Math.trunc(value);
            let result = intGroup(whole);
            if (value !== whole) {
                const rem = num - whole * den;
                let a = rem;
                let b = den;
                while (b) {
                    const c = a;
                    a = b;
                    b = c % b;
                }
                result += '\xa0' + intFlat1(Math.abs(rem / a)) + '/' + intFlat1(Math.abs(den / a));
            }
            return result;
        }

        /**
         * @function
         * @description Parse a locally formatted number with optional grouping separators.
         * @param {number} str - Number to parse.
         */
        $num.parse = rtl
            ? str => str === emDash ? void 0 : +str
                .replace('\u200e', '')
            : group == ',' && decimal == '.'
                ? str => str === emDash ? void 0 : +str
                    .replaceAll(',', '')
                : str => str === emDash ? void 0 : +str
                    .replaceAll(group, '')
                    .replaceAll(decimal, '.');

        /**
         * @function
         * @description Parse a locally formatted percentage with optional grouping separators.
         * @param {number} str - Number to parse.
         */
        $num.parsePercent = str => $num.parse(str
            .replaceAll(group, '')
            .replace(/\s*%$/, '')
            .replace(/(\d{0,2})([.,]|$)/, (a,b) => decimal + ('00' + b).slice(-2)));

        /**
         * @function
         * @description Parse a 1/n ticks value (Garban price).
         * @param {number} str - Number to parse.
         */
        $num.parseTicks = str => {
            const parts = str.match(/^((-?)\d*)\u200e?'?(\d?\d?)(\d?)0*$/);
            if (parts) {
                const whole = +parts[1];
                const fract = (parts[3].padEnd(2, '0') * 8 + [0,1,2,3,,4,5,6,7][+parts[4]]) / 256;
                if (fract < 1) {
                    return whole + (parts[2] ? -fract : fract);
                }
            }
            return NaN;
        }

        /* trigger re-bind of v-num elements */
        dirty++;
    }
});


export {
    dirty
};
export default $num;
